<template>
    <div
        class="button-container px-6 pt-4 pb-6 fixed md:static bottom-0 left-0 right-0 p-4 z-40">
        <div :class="[buttonWrapperClasses]">
            <button
                class="action-btn submit-button flex items-center justify-center space-x-2 p-4 relative flex-1 self-stretch flex-grow-1 text-text-on-background rounded-md overflow-hidden w-full"
                :class="[
                    isSubmitting
                        ? 'bg-[#D0D0D0] pointer-events-none'
                        : 'bg-[#e40032]',
                ]"
                :disabled="isSubmitting"
                @click="handleButtonClick">
                <div
                    class="text-wrapper relative var-font-family font-[--WEB-all-button-large-font-weight] text-[--colour-theme-text-text-on-background] var-line-height mt--1 w-fit whitespace-nowrap"
                    :style="buttonTextStyles">
                    {{ buttonData.text }}
                </div>
            </button>
        </div>
        <div
            class="mt-4 text-center text-sm"
            v-html="privacy"></div>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import * as apiRequests from '@/helpers/fetchData';
    import { getBookingFailureMessage } from '@/helpers/errorMessages';
    import { useBookingStore } from '@/stores/booking';
    import { useStateStore } from '@/stores/state';
    import { useAvailabilityStore } from '@/stores/booking-availability';
    import { useValidationStore } from '@/stores/validation';
    import { useErrorStore } from '@/stores/error';
    import { useRouter, useRuntimeConfig } from 'nuxt/app';
    import { getClientIpAddress } from '~/helpers/geoLocation';
    import { buildFbcFromFbclid, hasMarketingConsent } from '~/helpers/metaTracking';

    const VALIDATION_ERROR_MESSAGE =
        'Please ensure all required fields are correctly filled in.';

    const router = useRouter();

    const bookingStore = useBookingStore();
    const validationStore = useValidationStore();
    const stateStore = useStateStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    const isSubmitting = ref(false);
    const mode = stateStore.getMode();

    const props = defineProps({
        buttonData: {
            type: Object,
            required: true,
        },
        outline: Boolean,
        primary: String,
        secondary: String,
        buttonWrapperClasses: String,
        buttonTextStyles: String,
        page: String,
        privacy: String,
    });

    const getCookieValue = (name: string) => {
        const prefix = `${name}=`;
        const cookie = document.cookie
            .split('; ')
            .find((item) => item.startsWith(prefix));

        return cookie
            ? decodeURIComponent(cookie.slice(prefix.length))
            : '';
    };

    // Meta matching fields, same as book-now. Skipped when staff book for a client
    const setMetaMatchingFields = async () => {
        if (bookingStore.isStaffJourney()) return;

        const marketingConsentGranted = hasMarketingConsent();

        // Don't block the booking if the IP lookup fails
        const clientIpAddress = await getClientIpAddress().catch(() => '');

        bookingStore.updateBookingState({
            fbp: marketingConsentGranted ? getCookieValue('_fbp') : '',
            fbc: marketingConsentGranted
                ? getCookieValue('_fbc') || buildFbcFromFbclid()
                : '',
            client_ip_address: clientIpAddress,
            client_user_agent: navigator.userAgent,
            event_source_url: window.location.href,
        });
    };

    const handleButtonClick = async () => {
        stateStore.startLoading();
        isSubmitting.value = true;

        try {
            const { valid, issues } = apiRequests.isBookingValid(
                bookingStore.state
            );

            if (!valid) {
                validationStore.setZodErrors(issues);
                throw new Error(VALIDATION_ERROR_MESSAGE);
            }

            await setMetaMatchingFields();

            await apiRequests.saveConsultation(
                bookingStore,
                stateStore,
                availabilityStore,
                errorStore,
                runtimeConfig,
                mode
            );

            if(bookingStore.state.is_duplicate) return;
            router.push(props.buttonData.page);
        } catch (error: any) {
            console.error('error with update/creation of booking', error);

            // Keep validation messaging; replace API/technical failures with friendly copy
            const message =
                error?.message === VALIDATION_ERROR_MESSAGE
                    ? VALIDATION_ERROR_MESSAGE
                    : getBookingFailureMessage();

            errorStore.setErrorInternally({ message });
        } finally {
            isSubmitting.value = false;
            stateStore.endLoading();
        }
    };
</script>
